import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { TRANSITION_DURATION_FAST } from './common/transition';
import { TeTab } from './te-tab';

/**
 * Tabs Component
 *
 * @slot - Default slot for tabs and tab content
 */
@customElement('te-tabs')
export class TeTabs extends TeComponent {
  static get styles() {
    return [
      css`
        .te-tabs-options {
          display: grid;
          grid-auto-flow: column;
          grid-auto-rows: 40px;
          grid-gap: 0px;
          position: relative;
          width: fit-content;
        }

        .te-tabs-options-wrapper {
          position: relative;
        }

        .te-tabs-option-highlight-wrapper {
          position: absolute;
          transition-property: 'left';
          transition-duration: ${TRANSITION_DURATION_FAST}ms;
          left: 0;
          top: 0;
          height: 40px;
          margin: 2px;
          z-index: 1;
        }

        .te-tabs-option-highlight-wrapper .te-tabs-option-highlight {
          display: flex;
          width: calc(100% - 4px);
          box-shadow: ${theme.shadows.secondary};
          border-radius: 2px;
          height: 4px;
        }

        ::slotted(*) {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `,
    ];
  }

  /** Pre-styled tabs theme */
  @property({ type: String }) variant: 'filled' | 'underlined' = 'filled';
  @query('#te-tabs-options-slot') _slotContent?: HTMLSlotElement;
  @state() private _items = 1;

  getChildNodes(): TeTab[] {
    if (!this._slotContent) return [];
    const nodes = this._slotContent.assignedNodes() as TeTab[];
    return nodes.filter((node) => node.nodeType === 1);
  }

  firstUpdated() {
    this._items = this.getChildNodes().length;
  }

  renderHighlighter() {
    const options = this.getChildNodes();
    const index = options.findIndex((node) => node.active);
    if (index === -1) return;
    const left = (index * 100) / this._items;
    return html`
      <div
        class="te-tabs-option-highlight-wrapper"
        style=${styleMap(this.getHighlightWrapperStyle(left))}
      >
        <div class="te-tabs-option-highlight" style=${styleMap(this.getHighlightStyle())}></div>
      </div>
    `;
  }

  getHighlightWrapperStyle(left: number) {
    return {
      margin: '0px',
      top: 'auto',
      bottom: '-4px',
      height: '4px',
      left: `${left}%`,
      width: `${100 / this._items}%`,
    };
  }

  getHighlightStyle() {
    return {
      backgroundColor: `${theme.colors.primary.v0}`,
    };
  }

  getWrapperStyle() {
    switch (this.variant) {
      case 'underlined':
        return {
          borderBottom: '4px solid transparent',
          display: 'flex',
          flex: '1',
        };
      case 'filled':
      default:
        return {};
    }
  }

  getOptionsStyle() {
    switch (this.variant) {
      case 'underlined':
        return {
          boxShadow: 'none',
          borderRadius: '0px',
          background: 'transparent',
          gridTemplateColumns: `repeat(${this._items}, minmax(0, 1fr))`,
        };
      default:
        return {};
    }
  }

  render() {
    const isUL = this.variant === 'underlined';
    return html`
      <style>

        ::slotted(*) {
          border-radius: ${isUL ? '' : '4px 4px 0 0'};
          min-width: ${isUL ? '' : '224px'};
          background-color: ${isUL ? '' : theme.colors.shade1};
          margin-right: ${isUL ? '' : '1px'};
          z-index: ${isUL ? '1' : '0'};
          width: ${isUL ? '96px' : '0'};
        }

        ${(isUL &&
          `.te-tabs-options-wrapper::after {
          content: '';
          position: absolute;
          bottom: -4px;
          height: 4px;
          width: 100%;
          background-color: ${theme.colors.shade3};
          border-radius: 2px;
        }`) ||
        ''}
      </style>

      <div class="te-tabs-options-wrapper" style=${styleMap(this.getWrapperStyle())}>
        <div class="te-tabs-options" style=${styleMap(this.getOptionsStyle())}>
          <slot class="te-tabs-options-slot" id="te-tabs-options-slot"></slot>
          ${isUL ? this.renderHighlighter() : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-tabs': TeTabs;
  }
}
